import axios from "axios";
import { CONFIG } from "./enviromentConfig";
import { NetworkError, UnauthorizedError, DatabaseError } from "./ErrorConfigs";
import { GlobalRoutes } from "../../navigation/NavConstant";
import { setupCache } from "axios-cache-adapter";

const cache = setupCache({
  maxAge: 0 * 60 * 1000,
});

const instance = axios.create({
  baseURL: CONFIG["SERVER"],
  withCredentials: true,
  adapter: cache.adapter,
});

// --- ✅ Add a flag to prevent multiple refresh calls ---
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Function to notify all subscribers when new token is ready
function onRefreshed(newToken: string) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

instance.interceptors.request.use(
  (config) => {
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

/*
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    try {
      if (error.response) {
        const { status, data } = error.response;
        const newError = new DatabaseError(data);
        switch (status) {
          case 422:
            let message = newError.message;
            if (newError.code === "ER_DUP_ENTRY") {
              message = "Entry already exists";
            }

            return Promise.reject(newError);

          case 401:
            window.location.href = GlobalRoutes.LOGIN;
            localStorage.clear();
            return Promise.reject(
              new UnauthorizedError("Invalid Username or Password.")
            );

          case 500:
            return Promise.reject(newError);

          case 440:
            window.location.href = GlobalRoutes.LOGIN;
            break;

          default:
            return Promise.reject(data);
            break;
        }
        return Promise.reject(status);
      } else {
        return Promise.reject(new NetworkError());
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }
);
*/
// --- ✅ Response Interceptor with Refresh Logic ---
instance.interceptors.response.use(
  (response) => response,

  async (error) => {
    try {
      if (!error.response) {
        // Network or server down
        return Promise.reject(new NetworkError());
      }

      const { status, config, data } = error.response;
      const originalRequest = config;

      // --- CASE 401 or 440: Token expired ---
      if ((status === 401 || status === 440) && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          localStorage.clear();
          window.location.href = GlobalRoutes.LOGIN;
          return Promise.reject(new UnauthorizedError("Session expired."));
        }

        // If a refresh is already in progress, queue this request
        if (isRefreshing) {
          return new Promise((resolve) => {
            addRefreshSubscriber((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(instance(originalRequest));
            });
          });
        }

        isRefreshing = true;

        try {
          // --- Call your refresh-token API ---
          const refreshResponse = await axios.post(
            `${CONFIG["SERVER"]}refresh-token`,
            { refresh_token: refreshToken }
          );

          const newToken = refreshResponse.data?.data?.token;
          const newRefreshToken = refreshResponse.data?.data?.refresh_token;
          const tokenExpiresAt = refreshResponse.data?.data?.token_expires_at;
          const refreshTokenExpiresAt =
            refreshResponse.data?.data?.refresh_token_expires_at;

          if (newToken && newRefreshToken) {
            // Update localStorage
            localStorage.setItem("authToken", newToken);
            localStorage.setItem("refreshToken", newRefreshToken);
            if (tokenExpiresAt)
              localStorage.setItem("tokenExpiresAt", tokenExpiresAt);
            if (refreshTokenExpiresAt)
              localStorage.setItem(
                "refreshTokenExpiresAt",
                refreshTokenExpiresAt
              );

            // Notify queued requests
            onRefreshed(newToken);

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          } else {
            throw new Error("Invalid refresh token response");
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          localStorage.clear();
          window.location.href = GlobalRoutes.LOGIN;
          return Promise.reject(
            new UnauthorizedError("Session expired. Please log in again.")
          );
        } finally {
          isRefreshing = false;
        }
      }

      // --- Handle Validation Errors ---
      if (status === 422) {
        const newError = new DatabaseError(data);
        if (newError.code === "ER_DUP_ENTRY") {
          newError.message = "Entry already exists";
        }
        return Promise.reject(newError);
      }

      // --- Handle Other Errors ---
      if (status === 500) return Promise.reject(new DatabaseError(data));
      if (status === 440) {
        localStorage.clear();
        window.location.href = GlobalRoutes.LOGIN;
      }

      return Promise.reject(data);
    } catch (err) {
      return Promise.reject(err);
    }
  }
);

export default instance;
